import { useMemo } from 'react'
import {
  Box,
  Card,
  CardContent,
  IconButton,
  Typography,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import dayjs, { Dayjs } from 'dayjs'
import { useIntl } from 'react-intl'
import { messages } from '../../messages'
import { RouteDestination, LoadMetadata } from '../plannerTypes'
import { PlannerAction } from '../state/journeyPlannerReducer'
import LoadActionsTable from './LoadActionsTable'

type Service = {
  id: number
  name: string
}

type DestinationBoxProps = {
  destination: RouteDestination
  legIndex: number
  routeIndex: number
  destIndex: number
  loadMetadataById: Record<number, LoadMetadata>
  dispatch: React.Dispatch<PlannerAction>
  canRemove: boolean
  onEditClick?: () => void
  minDate?: Dayjs
  services?: Service[]
}

const DestinationBox = ({
  destination,
  legIndex,
  routeIndex,
  destIndex,
  loadMetadataById,
  dispatch,
  canRemove,
  onEditClick,
  minDate,
  services = [],
}: DestinationBoxProps) => {
  const { formatMessage } = useIntl()

  const handleRemoveDestination = () => {
    dispatch({
      type: 'REMOVE_DESTINATION_FROM_ROUTE',
      legIndex,
      routeIndex,
      destIndex,
    })
  }

  const handleDateChange = (newDate: Dayjs | null) => {
    if (newDate && newDate.isValid()) {
      dispatch({
        type: 'UPDATE_DESTINATION_DATE',
        legIndex,
        routeIndex,
        destIndex,
        scheduledDate: newDate.toISOString(),
      })
    }
  }

  const hasMultipleBookings = useMemo(() => {
    const bookingIds = new Set<number>()
    destination.loadActions.forEach((action) => {
      const metadata = loadMetadataById[action.loadId]
      if (metadata?.shipmentBookingId) {
        bookingIds.add(metadata.shipmentBookingId)
      }
    })
    return bookingIds.size > 1
  }, [destination.loadActions, loadMetadataById])

  const getBackgroundColor = () => {
    if (hasMultipleBookings) return 'warning.50'
    if (destination.isBoundary) return 'primary.50'
    return 'white'
  }

  const getBorderColor = () => {
    if (hasMultipleBookings) return 'warning.main'
    if (destination.isBoundary) return 'primary.main'
    return 'grey.300'
  }

  const scheduledDateValue = destination.scheduledDate
    ? dayjs(destination.scheduledDate)
    : null

  return (
    <Card
      variant="outlined"
      data-testid={`destination-box-${destIndex}`}
      sx={{
        minWidth: 250,
        bgcolor: getBackgroundColor(),
        borderColor: getBorderColor(),
      }}
    >
      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 'bold' }}
            data-testid={`destination-location-name-${destIndex}`}
          >
            {destination.locationName || formatMessage(messages.selectLocation)}
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {onEditClick && destination.loadActions.length > 0 && (
              <IconButton
                onClick={onEditClick}
                color="primary"
                size="small"
                aria-label={formatMessage(messages.editActionsServices)}
                data-testid={`destination-edit-icon-${destIndex}`}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            )}
            {canRemove && !destination.isBoundary && (
              <IconButton
                onClick={handleRemoveDestination}
                color="error"
                size="small"
                aria-label="Remove destination"
                data-testid={`destination-delete-icon-${destIndex}`}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        </Box>

        <Box sx={{ mb: 1 }} data-testid={`destination-date-picker-${destIndex}`}>
          <DatePicker
            label={formatMessage(messages.date)}
            value={scheduledDateValue}
            onChange={handleDateChange}
            minDate={minDate || dayjs().startOf('day')}
            format="YYYY-MM-DD"
            slotProps={{
              textField: {
                size: 'small',
                sx: { width: '100%' },
              },
            }}
          />
        </Box>

        <LoadActionsTable
          loadActions={destination.loadActions}
          loadMetadataById={loadMetadataById}
          loadServices={destination.loadServices}
          services={services}
        />
      </CardContent>
    </Card>
  )
}

export default DestinationBox
